import type { ApiError } from "./types";

/** Maximum analytics window the backend accepts (UI_CHANGELOG #2). */
export const MAX_WINDOW_DAYS = 92;

/** Pull the contract ApiError out of an unknown thrown value. Accepts both the
 *  `{ error: ApiError }` envelope and a bare ApiError; returns undefined for
 *  anything else (network blips, thrown strings, etc.). */
export function toApiError(err: unknown): ApiError | undefined {
  if (typeof err !== "object" || err === null) return undefined;

  const candidate = "error" in err ? (err as { error: unknown }).error : err;
  if (
    typeof candidate === "object" &&
    candidate !== null &&
    "code" in candidate &&
    "message" in candidate
  ) {
    return candidate as ApiError;
  }

  return undefined;
}

export interface FriendlyError {
  title: string;
  message: string;
}

/** Map an API error to a human title + next-step message, keyed on `error.code`
 *  (UI_CHANGELOG #2/#3). Falls back to the raw message for unknown codes. */
export function friendlyError(err: unknown): FriendlyError {
  const api = toApiError(err);
  if (!api) {
    return {
      title: "Couldn’t load this panel",
      message: "Something went wrong loading this data. Please retry.",
    };
  }

  const detail = (api.detail ?? undefined) as
    { max_days?: number; max_rows?: number } | undefined;

  switch (api.code) {
    case "window_too_large":
      return {
        title: "Time range too large",
        message: detail?.max_rows
          ? `This range matches over ${detail.max_rows.toLocaleString()} calls. Narrow the dates or add a model/session filter.`
          : `Choose a window of ${detail?.max_days ?? MAX_WINDOW_DAYS} days or fewer, or add a filter.`,
      };
    case "unauthorized":
      return {
        title: "Not authorized",
        message:
          "Your session has expired or isn’t authorized for analytics. Sign in again.",
      };
    case "forbidden":
      return {
        title: "No access",
        message:
          "You don’t have access to this workspace’s analytics. Switch workspaces or ask an admin for access.",
      };
    case "bad_request":
      return { title: "Couldn’t apply this view", message: api.message };
    case "not_found":
      return { title: "Not found", message: api.message };
    case "upstream":
      return {
        title: "Service unavailable",
        message:
          "Couldn’t reach the analytics service. Check the gateway and retry.",
      };
    default:
      return { title: "Couldn’t load this panel", message: api.message };
  }
}

/** Badge tone for a failed-request status (maps to the DS Badge tones). */
export type ErrorTone = "error" | "warning";

export interface StatusLabel {
  /** Short human label, e.g. "Rate limited", "Upstream unreachable". */
  label: string;
  tone: ErrorTone;
}

const STATUS_LABELS: Record<number, StatusLabel> = {
  400: { label: "Invalid request", tone: "warning" },
  401: { label: "Unauthorized", tone: "warning" },
  403: { label: "Forbidden", tone: "warning" },
  404: { label: "Not found", tone: "warning" },
  408: { label: "Timed out", tone: "warning" },
  413: { label: "Request too large", tone: "warning" },
  429: { label: "Rate limited", tone: "warning" },
  499: { label: "Client disconnected", tone: "warning" },
  500: { label: "Gateway error", tone: "error" },
  502: { label: "Upstream unreachable", tone: "error" },
  503: { label: "Upstream unavailable", tone: "error" },
  504: { label: "Upstream timeout", tone: "error" },
  529: { label: "Overloaded", tone: "error" },
};

/** Friendly label + badge tone for a failed-request status code. Falls back to
 *  a class-based label (4xx client / 5xx server) for unlisted codes. */
export function statusLabel(status: number): StatusLabel {
  const known = STATUS_LABELS[status];
  if (known) return known;

  if (status >= 500) return { label: "Server error", tone: "error" };

  if (status >= 400) return { label: "Client error", tone: "warning" };

  return { label: "Error", tone: "error" };
}
